/**
 * 录像/回放：输入流录制（逐逻辑步）+ 确定性回放。
 *
 * 确定性前提：
 * - 游戏逻辑只读 Input 单例，录像按逻辑步（1/60s）记录 down/justPressed 位掩码；
 * - 全部随机源走 engine/math.js 的可注入 RNG（mulberry32，局开始播种）；
 * - 逻辑为固定步长（loop.js），回放按步注入相同输入即逐帧一致。
 *
 * REPLAY_VERSION：游戏机制/内容有不兼容变更时递增，旧版本录像拒绝播放。
 * node 可直接 import（encode/decode/Recorder/Replayer 不触浏览器 API）。
 */

/** 录像格式版本（机制/内容变更时 +1，旧版本拒绝播放） */
export const REPLAY_VERSION = 17; // v17：sleep/confuse bomb 生效 + 对话计时语言无关 + pierce 同帧扣减
// 关于"符卡练习立刻失败"不需要 bump 的论证（用户定案）：
// ① 练习录像不上传、不参与服务端校验；② 旧规则下用雷/技能本就判脏=失败，
// 新规则只是让失败更早显示——旧录像在新行为下的记录结果（挑战+1、取得+0）
// 完全一致，不存在结果漂移。 // v17：sleep/confuse bomb 对正式内容生效（modsGate）+ 对话计时语言无关 + pierce 同帧扣减 // v16：技能差异化重做 // v15：新增大 boss 战前对话 // v14：timidBullet 负 homing/bossMove 游移/阶段计时器清零/旋风几何/accel 上限 + 贯通按目标去重等弹幕与命中行为修正

/**
 * 各模式可接受的最低录像版本（未列出的模式按 default）。
 * v12→v13 全关卡（1-6+Extra）弹幕重设计（MR !41）：小兵/中 boss/大 boss
 * 行为全面变更，任何模式的 v12 及以下录像在当前构建下均不再逐帧确定，一律拒绝。
 */
const REPLAY_MIN_VERSION = { default: 17, extra: 17 }; // v13 一律拒播（弹幕轨迹/命中结算变化影响确定性）

/**
 * 录像与当前构建的兼容性判定。
 * @param {{version?:number, mode?:string}} meta 录像元数据（mode 缺省按普通关）
 * @returns {boolean}
 */
export function isReplayCompatible(meta) {
  if (!meta || meta.version === undefined) return true; // 无版本戳的历史条目保持原行为（可播）
  const min = (meta.mode && Object.hasOwn(REPLAY_MIN_VERSION, meta.mode)) ? REPLAY_MIN_VERSION[meta.mode] : REPLAY_MIN_VERSION.default;
  return meta.version >= min && meta.version <= REPLAY_VERSION;
}

/** 录制的动作集合（位序号 = 掩码位） */
export const REPLAY_ACTIONS = [
  'up', 'down', 'left', 'right', 'shot', 'bomb', 'special', 'slow', 'pause', 'confirm', 'cancel',
];

/** 动作集合 → 位掩码。 */
export function toBits(actionSet) {
  let bits = 0;
  for (let i = 0; i < REPLAY_ACTIONS.length; i++) {
    if (actionSet.has(REPLAY_ACTIONS[i])) bits |= 1 << i;
  }
  return bits;
}

/** 位掩码 → 动作名数组。 */
export function fromBits(bits) {
  const out = [];
  for (let i = 0; i < REPLAY_ACTIONS.length; i++) {
    if (bits & (1 << i)) out.push(REPLAY_ACTIONS[i]);
  }
  return out;
}

/**
 * 输入流编码：每步 [down, pressed] 两路位掩码 → RLE → JSON。
 * 格式：{v, d:[[downBits,pressedBits,重复次数],...]}（输入极少变化，RLE 压缩率高）
 * @param {[number, number][]} frames
 */
export function encodeFrames(frames) {
  const d = [];
  for (const [down, pressed] of frames) {
    const last = d[d.length - 1];
    if (last && last[0] === down && last[1] === pressed) last[2]++;
    else d.push([down, pressed, 1]);
  }
  return JSON.stringify({ v: 1, d });
}

/**
 * 输入流解码（encodeFrames 逆过程）。
 * @param {string} str
 * @returns {[number, number][]}
 */
/** 展开帧数上限（与 storeReplay 的 MAX_FRAME_COUNT 对齐：3.6e4 秒 × 60） */
export const MAX_REPLAY_FRAMES = 216000;

export function decodeFrames(str) {
  // 输入不可信（localStorage / 网络）：逐条校验形状并给展开总量封顶，
  // 否则几十字节的恶意 RLE 可展开上亿帧直接 OOM（历史 bug：服务端校验进程崩溃循环）
  const { d } = JSON.parse(str);
  if (!Array.isArray(d)) throw new Error('replay frames: 缺 d 数组');
  const frames = [];
  for (const e of d) {
    if (!Array.isArray(e) || e.length !== 3) throw new Error('replay frames: 条目非三元组');
    const [down, pressed, n] = e;
    if (!Number.isInteger(down) || !Number.isInteger(pressed) || !Number.isInteger(n) || down < 0 || pressed < 0 || n < 0) {
      throw new Error('replay frames: 条目含非法值');
    }
    if (frames.length + n > MAX_REPLAY_FRAMES) throw new Error('replay frames: 展开超上限');
    for (let i = 0; i < n; i++) frames.push([down, pressed]);
  }
  return frames;
}

/**
 * 录制一局：enter 时构造，每个逻辑步调 capture()。
 * 产出 uploadPayload()（上传/本地存储用）。
 */
export class Recorder {
  /**
   * @param {{seed:number, character:string, difficulty:string, mode:string}} meta
   */
  constructor(meta) {
    this.meta = { ...meta, version: REPLAY_VERSION };
    /** @type {[number, number][]} */
    this.frames = [];
  }

  /**
   * 记录本逻辑步输入。
   * @param {{down: Set<string>, justPressed: Set<string>}} Input
   */
  capture(Input) {
    this.frames.push([toBits(Input.down), toBits(Input.justPressed)]);
  }

  /** 结束：补结算信息，产出可上传/存储的 payload。 */
  uploadPayload({ score, stageReached, cleared }) {
    return {
      ...this.meta,
      score,
      stageReached,
      cleared,
      frameCount: this.frames.length,
      frames: encodeFrames(this.frames),
    };
  }
}

/**
 * 回放驱动：每个逻辑步用录像输入覆写 Input 单例。
 * 播完后 hold 最后一帧输入（场景自然走到结算）。
 */
export class Replayer {
  /**
   * @param {string} framesStr encodeFrames 产物
   */
  constructor(framesStr) {
    this.frames = decodeFrames(framesStr);
    this.pos = 0;
  }

  /** 回放是否已播完录像输入。 */
  get done() {
    return this.pos >= this.frames.length;
  }

  /**
   * 覆写 Input 状态为本步录像值。
   * @param {{down: Set<string>, justPressed: Set<string>}} Input
   */
  apply(Input) {
    const done = this.pos >= this.frames.length;
    const f = done ? [this.frames[this.frames.length - 1]?.[0] ?? 0, 0] : this.frames[this.pos];
    Input.down.clear();
    Input.justPressed.clear();
    for (const a of fromBits(f[0])) Input.down.add(a);
    for (const a of fromBits(f[1])) Input.justPressed.add(a);
    if (!done) this.pos++;
  }
}
